import { useEffect } from "react";
import { useParams, useLocation, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { CTAButton, Eyebrow } from "@/components/site/ui";
import { SERVICE_DETAILS, SERVICES } from "@/data/content";

export default function ServicePage() {
  const location = useLocation();
  const params = useParams();
  const path = location.pathname;
  const data = SERVICE_DETAILS[path];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (data) document.title = `${data.name} | Dentro il Calice AI Winery`;
  }, [data, path]);

  if (!data) return <Navigate to="/" replace />;

  const others = SERVICES.packages.filter((p) => p.route !== path);

  return (
    <div className="bg-brand-cream min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative bg-brand-graphite grain-overlay pt-28 sm:pt-36 pb-16 lg:pb-24 overflow-hidden">
        <img
          src={data.image}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-graphite via-brand-graphite/85 to-brand-bordeaux/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <a
              href="/#servizi"
              data-testid="service-back-link"
              className="inline-flex items-center gap-2 text-sm text-brand-ivory/70 hover:text-brand-gold transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Tutti i servizi
            </a>
          </Reveal>
          <div className="mt-8">
            <Reveal><Eyebrow>{data.eyebrow}</Eyebrow></Reveal>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-brand-ivory"
              data-testid="service-title"
            >
              {data.name}
            </motion.h1>
            <Reveal delay={1}>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-brand-ivory/80">{data.tagline}</p>
            </Reveal>
            <Reveal delay={2}>
              <div className="mt-8 flex flex-wrap items-baseline gap-x-4 gap-y-2">
                <span className="font-serif text-4xl text-brand-gold">{data.price}</span>
                <span className="text-sm text-brand-ivory/70">{data.priceNote}</span>
                {data.duration && (
                  <span className="font-mono text-xs text-brand-ivory/55 border-l border-brand-ivory/20 pl-4">
                    Durata: {data.duration}
                  </span>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="relative py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-serif text-2xl sm:text-3xl leading-snug text-brand-bordeaux">{data.intro}</p>
            </Reveal>

            {data.sections.map((sec) => (
              <Reveal key={sec.h} delay={1}>
                <div className="mt-12">
                  <h2 className="font-serif text-2xl text-brand-graphite">{sec.h}</h2>
                  <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {sec.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 text-sm text-brand-graphite/80">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}

            {data.output && (
              <Reveal delay={1}>
                <div className="mt-12 rounded-sm border-l-2 border-brand-gold bg-brand-ivory p-6">
                  <span className="text-[10px] uppercase tracking-editorial font-semibold text-brand-gold">Output</span>
                  <p className="mt-2 text-base leading-relaxed text-brand-graphite/80">{data.output}</p>
                </div>
              </Reveal>
            )}

            {data.note && (
              <Reveal delay={1}>
                <p className="mt-8 text-sm leading-relaxed text-brand-graphite/60 italic">{data.note}</p>
              </Reveal>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-5">
            <Reveal delay={1}>
              <div className="lg:sticky lg:top-28 rounded-sm bg-brand-bordeaux p-8 grain-overlay">
                <h3 className="font-serif text-2xl text-brand-ivory">A chi è rivolto</h3>
                <p className="mt-4 text-sm leading-relaxed text-brand-ivory/80">{data.forWho}</p>
                <div className="mt-8 border-t border-brand-ivory/15 pt-6">
                  <p className="text-sm text-brand-ivory/70">
                    Ogni percorso parte da una diagnosi gratuita di 20–30 minuti, senza impegno.
                  </p>
                  <div className="mt-5">
                    <CTAButton href="/#lead" variant="gold" className="w-full" data-testid="service-detail-cta">
                      {data.ctaLabel}
                    </CTAButton>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="bg-brand-ivory py-16 lg:py-20 grain-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="font-serif text-2xl text-brand-graphite">Altri percorsi</h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {others.map((p) => (
              <Link
                key={p.route}
                to={p.route}
                data-testid={`other-service-${p.route.replace("/", "")}`}
                className="group rounded-sm border border-brand-bordeaux/12 bg-brand-cream p-6 transition-all hover:-translate-y-1 hover:border-brand-bordeaux/30"
              >
                <h3 className="font-serif text-xl text-brand-bordeaux">{p.name}</h3>
                <p className="mt-2 font-mono text-xs text-brand-graphite/55">{p.price} {p.priceNote}</p>
                <span className="mt-4 inline-block text-sm text-brand-bordeaux group-hover:text-brand-gold transition-colors">
                  Scopri →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
